import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Checkbox } from '@heyroot/ui/primitives/Checkbox';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { Drawer } from '@heyroot/ui/primitives/Drawer';
import {
  fetchTenantOrganization,
  fetchOrgLocations,
  fetchOrgProfitCenters,
  fetchOrgTerminals,
  createOrgLocation,
  updateOrgLocation,
  createOrgProfitCenter,
  updateOrgProfitCenter,
  createOrgTerminal,
  updateOrgTerminal,
  queryKeys,
  AdminApiError,
  type WireProfitCenterRow,
  type WireTerminalRow,
} from '../../lib/api';

// Exemplar: docs/design-system/exemplars -- nested drill-down list, closest
// to the settings module's location -> profit-center -> terminal hierarchy
// (three nested tables, per spec §7.2), rendered here as expand/collapse rows
// rather than a route-per-level so an operator never leaves the tab.
//
// Status vocabularies differ by level (spec §2.8) -- locations have FOUR
// values (active|inactive|closed|archived), profit centers and terminals have
// THREE (no 'closed'). Two separate badge maps below; never share one.
function locationStatusVariant(s: string): 'success' | 'warning' | 'neutral' | 'danger' {
  switch (s) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'warning';
    case 'closed':
      return 'danger';
    default:
      return 'neutral'; // archived
  }
}
function threeValueStatusVariant(s: string): 'success' | 'warning' | 'neutral' {
  switch (s) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'warning';
    default:
      return 'neutral'; // archived
  }
}

interface DrawerFormState {
  kind: 'location' | 'profit-center' | 'terminal';
  mode: 'create' | 'edit';
  locationId?: string;
  profitCenterId?: string;
  existing?: { id: string; name: string; status: string; version: number };
}

export function OrganizationTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set());
  const [expandedProfitCenters, setExpandedProfitCenters] = useState<Set<string>>(new Set());
  const [drawer, setDrawer] = useState<DrawerFormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [locationEditPending, setLocationEditPending] = useState<string | null>(null);

  // The counts endpoint (GET /organization) has no `version` field -- it is
  // the fast aggregate path, not the edit-drawer's data source. Fetch the
  // FULL location row (which has it) lazily, only when Edit is clicked, same
  // shape as the profit-center/terminal drill-down fetches below.
  async function openLocationEditDrawer(locationId: string, name: string) {
    setLocationEditPending(locationId);
    try {
      const { rows } = await qc.fetchQuery({
        queryKey: queryKeys.tenantOrgLocations(tenantId, includeArchived),
        queryFn: () => fetchOrgLocations(tenantId, includeArchived),
      });
      const row = rows.find((r) => r.id === locationId);
      if (!row) {
        setFormError('This location could not be loaded. Reload and try again.');
        return;
      }
      setDrawer({
        kind: 'location',
        mode: 'edit',
        existing: { id: row.id, name: row.name, status: row.status, version: row.version },
      });
    } catch {
      setFormError(`Could not load "${name}" for editing. Try again.`);
    } finally {
      setLocationEditPending(null);
    }
  }

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.tenantOrganization(tenantId, includeArchived),
    queryFn: () => fetchTenantOrganization(tenantId, includeArchived),
  });

  // Fix round 2 (I9): openLocationEditDrawer's failure path set formError
  // and returned WITHOUT opening the drawer -- but formError only renders
  // inside OrgEntityForm, which only mounts while the drawer is open. The
  // operator clicked Edit and saw literally nothing. Surface it here instead,
  // outside the (closed) drawer.
  const preDrawerError = drawer === null ? formError : null;

  function toggleLocation(id: string) {
    setExpandedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleProfitCenter(id: string) {
    setExpandedProfitCenters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ['tenant-organization', tenantId] });
    void qc.invalidateQueries({ queryKey: ['tenant-org-locations', tenantId] });
    void qc.invalidateQueries({ queryKey: ['tenant-org-profit-centers', tenantId] });
    void qc.invalidateQueries({ queryKey: ['tenant-org-terminals', tenantId] });
  }

  const saveMutation = useMutation({
    mutationFn: async (vars: {
      kind: DrawerFormState['kind'];
      mode: 'create' | 'edit';
      name: string;
      id?: string;
      version?: number;
      locationId?: string;
      profitCenterId?: string;
    }) => {
      if (vars.kind === 'location') {
        // Fix round 2 (I9): hardcoding timezone/currencyCode here is worse
        // than omitting them -- a tenant with a real timezone got a UTC
        // location, silently shifting every business date computed for it.
        // The module's CreateLocationInputSchema already defaults BOTH
        // (timezone='UTC', currencyCode='USD') when omitted -- see
        // modules/tenants/schemas/locations.zod.ts's own header comment.
        return vars.mode === 'create'
          ? createOrgLocation(tenantId, { name: vars.name })
          : updateOrgLocation(tenantId, vars.id!, vars.version!, { name: vars.name });
      }
      if (vars.kind === 'profit-center') {
        return vars.mode === 'create'
          ? createOrgProfitCenter(tenantId, { name: vars.name, locationId: vars.locationId })
          : updateOrgProfitCenter(tenantId, vars.id!, vars.version!, { name: vars.name });
      }
      return vars.mode === 'create'
        ? createOrgTerminal(tenantId, { name: vars.name, profitCenterId: vars.profitCenterId })
        : updateOrgTerminal(tenantId, vars.id!, vars.version!, { name: vars.name });
    },
    onSuccess: () => {
      setFormError(null);
      setDrawer(null);
      invalidateAll();
    },
    onError: (err) => {
      setFormError(err instanceof AdminApiError ? err.message : 'Could not save. Try again.');
    },
  });

  if (isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-[85%]" />
        <Skeleton className="h-10 w-[70%]" />
      </div>
    );
  }
  if (isError) {
    return (
      <ErrorState
        title="Could not load the organization"
        description="Locations, profit centers and terminals failed to load."
        onRetry={() => void refetch()}
      />
    );
  }

  const locations = data?.locations ?? [];

  return (
    <div className="flex flex-col gap-4">
      {preDrawerError && (
        <p role="alert" className="text-body-sm text-danger-text">
          {preDrawerError}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-body-sm text-secondary">
          <Checkbox
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Include archived
        </label>
        {canWrite && (
          <Button variant="primary" onClick={() => setDrawer({ kind: 'location', mode: 'create' })}>
            Add location
          </Button>
        )}
      </div>

      {locations.length === 0 ? (
        <EmptyState
          title="No locations yet"
          description="Add the tenant's first location to start building out its organization."
        />
      ) : (
        <div className="overflow-x-auto rounded-control border border-border">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell />
                <TableHeaderCell>Location</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Profit centers</TableHeaderCell>
                <TableHeaderCell>Terminals</TableHeaderCell>
                {canWrite && <TableHeaderCell />}
              </tr>
            </TableHead>
            <TableBody>
              {locations.map((loc) => (
                <LocationRows
                  key={loc.id}
                  tenantId={tenantId}
                  loc={loc}
                  expanded={expandedLocations.has(loc.id)}
                  onToggle={() => toggleLocation(loc.id)}
                  expandedProfitCenters={expandedProfitCenters}
                  onToggleProfitCenter={toggleProfitCenter}
                  includeArchived={includeArchived}
                  canWrite={canWrite}
                  onEditLocation={() => void openLocationEditDrawer(loc.id, loc.name)}
                  editPending={locationEditPending === loc.id}
                  onAddProfitCenter={() =>
                    setDrawer({ kind: 'profit-center', mode: 'create', locationId: loc.id })
                  }
                  onEditProfitCenter={(pc) =>
                    setDrawer({
                      kind: 'profit-center',
                      mode: 'edit',
                      locationId: loc.id,
                      existing: {
                        id: pc.id,
                        name: pc.name,
                        status: pc.status,
                        version: pc.version,
                      },
                    })
                  }
                  onAddTerminal={(pcId) =>
                    setDrawer({ kind: 'terminal', mode: 'create', profitCenterId: pcId })
                  }
                  onEditTerminal={(t) =>
                    setDrawer({
                      kind: 'terminal',
                      mode: 'edit',
                      existing: { id: t.id, name: t.name, status: t.status, version: t.version },
                    })
                  }
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Drawer
        open={drawer !== null}
        onClose={() => {
          setDrawer(null);
          setFormError(null);
        }}
        title={
          drawer
            ? `${drawer.mode === 'create' ? 'Add' : 'Edit'} ${drawer.kind.replace('-', ' ')}`
            : ''
        }
      >
        {drawer && (
          <OrgEntityForm
            key={`${drawer.kind}-${drawer.existing?.id ?? 'new'}`}
            state={drawer}
            error={formError}
            pending={saveMutation.isPending}
            onCancel={() => {
              setDrawer(null);
              setFormError(null);
            }}
            onSubmit={(name) =>
              saveMutation.mutate({
                kind: drawer.kind,
                mode: drawer.mode,
                name,
                ...(drawer.existing?.id !== undefined ? { id: drawer.existing.id } : {}),
                ...(drawer.existing?.version !== undefined
                  ? { version: drawer.existing.version }
                  : {}),
                ...(drawer.locationId !== undefined ? { locationId: drawer.locationId } : {}),
                ...(drawer.profitCenterId !== undefined
                  ? { profitCenterId: drawer.profitCenterId }
                  : {}),
              })
            }
          />
        )}
      </Drawer>
    </div>
  );
}

function OrgEntityForm({
  state,
  error,
  pending,
  onCancel,
  onSubmit,
}: {
  state: DrawerFormState;
  error: string | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(state.existing?.name ?? '');
  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="text-body-sm text-danger-text">
          {error}
        </p>
      )}
      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm-emphasis text-secondary">Name</span>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>
      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={!name.trim() || pending}
          loading={pending}
          onClick={() => onSubmit(name.trim())}
        >
          {state.mode === 'create' ? 'Create' : 'Save'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function LocationRows({
  tenantId,
  loc,
  expanded,
  onToggle,
  expandedProfitCenters,
  onToggleProfitCenter,
  includeArchived,
  canWrite,
  onEditLocation,
  editPending,
  onAddProfitCenter,
  onEditProfitCenter,
  onAddTerminal,
  onEditTerminal,
}: {
  tenantId: string;
  loc: {
    id: string;
    name: string;
    status: string;
    profitCentersCount: number;
    terminalsCount: number;
  };
  expanded: boolean;
  onToggle: () => void;
  expandedProfitCenters: Set<string>;
  onToggleProfitCenter: (id: string) => void;
  includeArchived: boolean;
  canWrite: boolean;
  onEditLocation: () => void;
  editPending: boolean;
  onAddProfitCenter: () => void;
  onEditProfitCenter: (pc: WireProfitCenterRow) => void;
  onAddTerminal: (profitCenterId: string) => void;
  onEditTerminal: (t: WireTerminalRow) => void;
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.tenantOrgProfitCenters(tenantId, loc.id, includeArchived),
    queryFn: () => fetchOrgProfitCenters(tenantId, loc.id, includeArchived),
    enabled: expanded,
  });

  return (
    <>
      <TableRow className="hover:bg-surface-elevated transition-colors">
        <TableCell>
          <button
            type="button"
            aria-label={expanded ? `Collapse ${loc.name}` : `Expand ${loc.name}`}
            onClick={onToggle}
            className="text-muted hover:text-primary"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </TableCell>
        <TableCell className="text-body text-primary">{loc.name}</TableCell>
        <TableCell>
          <Badge variant={locationStatusVariant(loc.status)}>{loc.status}</Badge>
        </TableCell>
        <TableCell className="tabular-nums">{loc.profitCentersCount}</TableCell>
        <TableCell className="tabular-nums">{loc.terminalsCount}</TableCell>
        {canWrite && (
          <TableCell>
            <Button variant="ghost" loading={editPending} onClick={onEditLocation}>
              Edit
            </Button>
          </TableCell>
        )}
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={canWrite ? 5 : 4}>
            <div className="pl-4 py-2 flex flex-col gap-2">
              {canWrite && (
                <Button variant="link" className="self-start" onClick={onAddProfitCenter}>
                  Add profit center
                </Button>
              )}
              {isPending ? (
                <Skeleton className="h-8 w-full" />
              ) : isError ? (
                <p className="text-body-sm text-danger-text">Could not load profit centers.</p>
              ) : (data?.rows.length ?? 0) === 0 ? (
                <p className="text-body-sm text-muted">No profit centers under this location.</p>
              ) : (
                <Table dense>
                  <TableHead>
                    <tr>
                      <TableHeaderCell />
                      <TableHeaderCell>Profit center</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      {canWrite && <TableHeaderCell />}
                    </tr>
                  </TableHead>
                  <TableBody>
                    {data!.rows.map((pc) => (
                      <ProfitCenterRows
                        key={pc.id}
                        tenantId={tenantId}
                        pc={pc}
                        expanded={expandedProfitCenters.has(pc.id)}
                        onToggle={() => onToggleProfitCenter(pc.id)}
                        includeArchived={includeArchived}
                        canWrite={canWrite}
                        onEdit={() => onEditProfitCenter(pc)}
                        onAddTerminal={() => onAddTerminal(pc.id)}
                        onEditTerminal={onEditTerminal}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ProfitCenterRows({
  tenantId,
  pc,
  expanded,
  onToggle,
  includeArchived,
  canWrite,
  onEdit,
  onAddTerminal,
  onEditTerminal,
}: {
  tenantId: string;
  pc: WireProfitCenterRow;
  expanded: boolean;
  onToggle: () => void;
  includeArchived: boolean;
  canWrite: boolean;
  onEdit: () => void;
  onAddTerminal: () => void;
  onEditTerminal: (t: WireTerminalRow) => void;
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.tenantOrgTerminals(tenantId, pc.id, includeArchived),
    queryFn: () => fetchOrgTerminals(tenantId, pc.id, includeArchived),
    enabled: expanded,
  });

  return (
    <>
      <TableRow className="hover:bg-surface-elevated transition-colors">
        <TableCell>
          <button
            type="button"
            aria-label={expanded ? `Collapse ${pc.name}` : `Expand ${pc.name}`}
            onClick={onToggle}
            className="text-muted hover:text-primary"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </TableCell>
        <TableCell className="text-body text-primary">{pc.name}</TableCell>
        <TableCell>
          <Badge variant={threeValueStatusVariant(pc.status)}>{pc.status}</Badge>
        </TableCell>
        {canWrite && (
          <TableCell>
            <Button variant="ghost" onClick={onEdit}>
              Edit
            </Button>
          </TableCell>
        )}
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={canWrite ? 3 : 2}>
            <div className="pl-4 py-2 flex flex-col gap-2">
              {canWrite && (
                <Button variant="link" className="self-start" onClick={onAddTerminal}>
                  Add terminal
                </Button>
              )}
              {isPending ? (
                <Skeleton className="h-8 w-full" />
              ) : isError ? (
                <p className="text-body-sm text-danger-text">Could not load terminals.</p>
              ) : (data?.rows.length ?? 0) === 0 ? (
                <p className="text-body-sm text-muted">No terminals under this profit center.</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {data!.rows.map((t) => (
                    <li key={t.id} className="flex items-center gap-3 text-body-sm">
                      <span className="text-primary min-w-0 truncate">{t.name}</span>
                      <Badge variant={threeValueStatusVariant(t.status)}>{t.status}</Badge>
                      {canWrite && (
                        <Button variant="link" onClick={() => onEditTerminal(t)}>
                          Edit
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
